import React, { useState, useEffect, useRef } from "react";
import {
  Input,
  Button,
  SimpleTable,
  useToast,
} from "@swordsweeper/ssi-ui-component-library";
import Modal, { ModalSizes } from "@src/components/common/Modal";
import FormContainer from "@src/components/containers/FormContainer/FormContainer";
import AddHiddenItemForm, {
  schema as AddHiddenItemFormSchema,
} from "@src/components/forms/AddHiddenItemForm";
import * as UserSettingsApi from "@src/api/UserSettingsApi";
import SpinningLoader from "@src/components/common/SpinningLoader";
import debounce from "lodash/debounce";
import Menu, { MenuItem, MenuThemes } from "@src/components/common/Menu";

interface HiddenItem {
  id: string;
  name: string;
  itemType: string;
}

export default function AccountHiddenItemSettings(): React.JSX.Element {
  const { showToast } = useToast();
  const [showAddHiddenItemModal, setShowAddHiddenItemModal] = useState(false);
  const [isCreatingHiddenItem, setIsCreatingHiddenItem] = useState(false);
  const [isHiddenItemCreationSuccessful, setIsHiddenItemCreationSuccessful] =
    useState(false);
  const [isLoadingHiddenItems, setIsLoadingHiddenItems] = useState(false);
  const [isDeletingHiddenItem, setIsDeletingHiddenItem] = useState(false);
  const [hiddenItems, setHiddenItems] = useState<HiddenItem[]>([]);
  const [search, setSearch] = useState<string | null>(null);

  useEffect(() => {
    debouncedLoadHiddenItems(search);
  }, [search]);

  const debouncedLoadHiddenItems = useRef(
    debounce(async (search: string | null) => {
      setIsLoadingHiddenItems(true);
      try {
        const response = await UserSettingsApi.getHiddenItems({ search });
        setHiddenItems(response.body);
      } catch (e) {
        console.error(e);
        showToast({
          message: "Error loading hidden items",
          variant: "error",
        });
      } finally {
        setIsLoadingHiddenItems(false);
      }
    }, 500),
  ).current;

  const handleAddRule = async (formData: any) => {
    setIsCreatingHiddenItem(true);
    try {
      const response = await UserSettingsApi.createHiddenItem(formData);
      const updatedHiddenItems = [...hiddenItems, response.body];
      setHiddenItems(updatedHiddenItems);
      setIsHiddenItemCreationSuccessful(true);
      showToast({
        message: "Hidden item rule created",
        variant: "success",
      });
    } catch (e) {
      console.error(e);
      showToast({
        message: "Error creating hidden item rule",
        variant: "error",
      });
    } finally {
      setIsCreatingHiddenItem(false);
    }
  };

  const handleDeleteRule = async (hiddenItemId: string) => {
    setIsDeletingHiddenItem(true);
    try {
      const response = await UserSettingsApi.deleteHiddenItem(hiddenItemId);
      const updatedHiddenItems = [...hiddenItems].filter(
        ({ id }) => id !== hiddenItemId,
      );
      setHiddenItems(updatedHiddenItems);
      showToast({
        message: "Item will no longer be hidden",
        variant: "success",
      });
    } catch (e) {
      console.error(e);
      showToast({
        message: "Error deleting hidden item rule",
        variant: "error",
      });
    } finally {
      setIsDeletingHiddenItem(false);
    }
  };

  const handleCloseAddHiddenItemModal = () => {
    setShowAddHiddenItemModal(false);
    setIsHiddenItemCreationSuccessful(false);
  };

  return (
    <div>
      {(isLoadingHiddenItems ||
        isCreatingHiddenItem ||
        isDeletingHiddenItem) && <SpinningLoader />}

      <div className="flex-row flex-center space-between">
        <div className="flex-row flex-center spaced-content-lg">
          <div
            className="text-lg font-weight-bold padding-y-lg"
            data-testid="account-hidden-item-settings-header"
          >
            Manage Hidden Items
          </div>
          <span
            className="text-sm"
            data-testid="account-hidden-item-settings-subheader"
          >
            Hide items you don't want to see when you shop.
          </span>
        </div>

        <div className="flex-row flex-center spaced-content">
          <Input
            defaultValue=""
            onChange={setSearch}
            placeholder="Find in list..."
            value={search || ""}
            width={240}
            height={34}
            icon={<i className="fa-light fa-magnifying-glass" />}
            data-testid="account-hidden-item-settings-search-input"
          />
          <Button
            id="account-hidden-item-settings-add-rule-button"
            className="btn btn-primary btn-md"
            onClick={() => setShowAddHiddenItemModal(true)}
            data-testid="account-hidden-item-settings-add-rule-button"
          >
            Add Rule
          </Button>
        </div>
      </div>
      <SimpleTable
        columns={[
          {
            header: "Name",
            key: "name",
          },
          {
            header: "Type",
            key: "itemType",
          },
          {
            header: "Action",
            key: "actions",
            render: ({ id }: HiddenItem) => (
              <span className="padding-left-sm">
                <Menu
                  buttonContent={
                    <div className="padding-x-sm">
                      <i className="fa-light fa-ellipsis-v" />
                    </div>
                  }
                  theme={MenuThemes.Small}
                >
                  <MenuItem>
                    <Button
                      className="width-100"
                      label="Delete"
                      color="#F80A33"
                      hoverColor="#FF98AA"
                      onClick={() => handleDeleteRule(id)}
                      data-testid={`account-hidden-item-settings-delete-button-${id}`}
                    />
                  </MenuItem>
                </Menu>
              </span>
            ),
          },
        ]}
        data={hiddenItems}
      />

      <Modal
        isShowing={showAddHiddenItemModal}
        size={ModalSizes.Small}
        onClose={handleCloseAddHiddenItemModal}
        header={
          isHiddenItemCreationSuccessful
            ? "You got it!"
            : "What would you like to hide?"
        }
      >
        <FormContainer
          form={AddHiddenItemForm}
          schema={AddHiddenItemFormSchema}
          formData={{}}
          additionalFormProps={{
            isSuccessful: isHiddenItemCreationSuccessful,
          }}
          onCancel={handleCloseAddHiddenItemModal}
          onSubmit={handleAddRule}
          submitButtonText="Hide This"
        />
      </Modal>
    </div>
  );
}
